import { useEffect } from "react";
import type { TableProps } from "antd";
import { Button, Modal, Space, Table, Typography, Grid } from "antd";
import { Check } from "lucide-react";
import type { ApplicationItem } from "@/types/Application/application";
import { DetailsApplicationModal } from "./DetailsApplicationModal";
import { RejectDetailsApplicationModal } from "./RejectDetailsApplicationModal";
import { RejectApplicationModal } from "./RejectApplicationModal";
import { AcceptApplicationModal } from "./AcceptApplicationModal";
import { EditApplicationModal } from "./EditApplicationModal";
import { DeleteApplicationModal } from "./DeleteApplicationModal";
import { useVillageList } from "./hook/useVillageList";
import { useApplicationModal } from "./hook/useApplicationModal";
import { formatValue } from "@/utils/formatValue";
import { renderNameCell } from "@/features/admin/hook/renderNameCell";
import { safeFormatyyyyMMddDateTime } from "@/utils/formatDate";

interface AllApplicationTable {
  applications: ApplicationItem[];
  loading: boolean;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  resetKey?: number;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
  onStatusFilterChange?: (value: string | null) => void;
}

const { Title } = Typography;
const { useBreakpoint } = Grid;

function getStatusText(status: string) {
  switch (status) {
    case "pending":
      return "Sedang Diproses";

    case "approve":
      return "Lulus";

    case "rejected":
      return "Ditolak";

    default:
      return status;
  }
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case "approve":
      return "bg-[#1F993B] text-white";

    case "pending":
      return "bg-[#FECD39] text-[#333]";

    case "rejected":
      return "bg-red-600 text-white";

    default:
      return "bg-gray-200 text-gray-700";
  }
}

export function AllApplicationTable({
  applications,
  loading,
  totalCount = 0,
  currentPage = 1,
  pageSize = 25,
  resetKey = 0,
  onPageChange,
  onRefresh,
  onStatusFilterChange,
}: AllApplicationTable) {
  const { fetchVillageList, villageList } = useVillageList();
  

  const {
    selectedRow,
    detailData,
    resultType,

    isDetailModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    isAcceptModalOpen,
    isRejectModalOpen,
    resultModalOpen,

    handleOpenModal,
    handleCloseModal,

    handleOpenEditModal,
    handleCloseEditModal,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleBackFromDeleteModal,
    handleDeleteSuccess,
    handleBackToDetailModal,

    handleOpenAcceptModal,
    handleCloseAcceptModal,
    handleBackFromAcceptModal,
    handleAcceptSuccess,

    handleOpenRejectModal,
    handleCloseRejectModal,
    handleBackFromRejectModal,
    handleRejectSuccess,

    handleCloseResultModal,
  } = useApplicationModal();

  const screens = useBreakpoint();

  useEffect(() => {
    fetchVillageList();
  }, []);

  const isRejectedStatus = selectedRow?.registrationStatus === "rejected";

  const columns: TableProps<ApplicationItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "registrationCode",
      sorter: (a, b) =>
        a.registrationCode.localeCompare(b.registrationCode, undefined, {
          numeric: true,
        }),
      defaultSortOrder: "ascend",
    },
    {
      title: "Status",
      dataIndex: "registrationStatus",
      filters: [
        { text: "Sedang Diproses", value: "pending" },
        { text: "Lulus", value: "approve" },
        { text: "Ditolak", value: "rejected" },
      ],
      onFilter: (value, record) =>
        record.registrationStatus === String(value),
      render: (status: string) => (
        <span
          className={`inline-flex items-center justify-center rounded-full px-3 py-1 w-32 text-xs font-semibold ${getStatusBadgeClass(
            status,
          )}`}
        >
          {getStatusText(status)}
        </span>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (value, record) => (
        <span
          className="block cursor-pointer"
          onClick={() => handleOpenModal(record)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleOpenModal(record);
            }
          }}
          role="button"
          tabIndex={0}
        >
          {renderNameCell(value)}
        </span>
      ),
    },
    {
      title: "MyKad/Passport",
      dataIndex: "identityNo",
      sorter: (a, b) => a.identityNo.localeCompare(b.identityNo),
      render: (value) => formatValue(value),
    },
    {
      title: "Jantina",
      dataIndex: "gender",
      filters: [
        { text: "Lelaki", value: "LELAKI" },
        { text: "Perempuan", value: "PEREMPUAN" },
      ],
      onFilter: (value, record) => record.gender === String(value),
    },
    {
      title: "Kategori",
      dataIndex: "residencyStatus",
      sorter: (a, b) => a.residencyStatus.localeCompare(b.residencyStatus),
      render: (value) => formatValue(value),
    },
    {
          title: "Tarikh Daftar",
          dataIndex: "submittedOn",
          sorter: (a, b) => {
            const aDate = new Date(a.submittedOn).getTime();
            const bDate = new Date(b.submittedOn).getTime();
    
            return aDate - bDate;
          },
          render: (value) => safeFormatyyyyMMddDateTime(value),
        },
    {
      title: "Tindakan",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            className="px-4 rounded-lg"
            onClick={() => handleOpenModal(record)}
          >
            Lihat
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
        <Table<ApplicationItem>
          key={resetKey}
          size="middle"
          showHeader
          tableLayout="fixed"
          className="c-table"
          columns={columns}
          dataSource={applications}
          loading={loading}
          rowKey="registrationId"
          scroll={{ x: "max-content" }}
          onChange={(_, filters) => {
            const rawStatus = Array.isArray(filters.registrationStatus)
              ? filters.registrationStatus[0]
              : undefined;

            const normalizedStatus =
              rawStatus == null || rawStatus === "all" || rawStatus === true
                ? null
                : String(rawStatus);

            onStatusFilterChange?.(normalizedStatus);
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            onChange: onPageChange,
            disabled: loading,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `Memaparkan ${range[0]}-${range[1]} daripada ${total}`,
          }}
        />
      </div>

      {selectedRow && isRejectedStatus ? (
        <RejectDetailsApplicationModal
          open={isDetailModalOpen}
          selectedRow={selectedRow}
          detailData={detailData}
          onClose={handleCloseModal}
          onEdit={handleOpenEditModal}
        />
      ) : (
        <DetailsApplicationModal
          open={isDetailModalOpen}
          selectedRow={selectedRow}
          detailData={detailData}
          onClose={handleCloseModal}
          onEdit={handleOpenEditModal}
          onAccept={handleOpenAcceptModal}
          onReject={handleOpenRejectModal}
          showActionButtons={selectedRow?.registrationStatus === "pending"}
        />
      )}

      <DeleteApplicationModal
        open={isDeleteModalOpen}
        registrationId={selectedRow?.registrationId ?? detailData?.registrationId ?? ""}
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        selectedRow={selectedRow}
        detailData={detailData}
        onClose={handleCloseDeleteModal}
        onBack={handleBackFromDeleteModal}
        onConfirm={handleDeleteSuccess}
        onSuccess={onRefresh}
      />

      <EditApplicationModal
        open={isEditModalOpen}
        application={detailData}
        villageList={villageList ?? []}
        onClose={handleCloseEditModal}
        onBack={handleBackToDetailModal}
        onDelete={handleOpenDeleteModal}
        onSuccess={onRefresh}
      />

      <AcceptApplicationModal
        open={isAcceptModalOpen}
        registrationId={
          selectedRow?.registrationId ?? detailData?.registrationId ?? ""
        }
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        onClose={handleCloseAcceptModal}
        onBack={handleBackFromAcceptModal}
        onConfirm={handleAcceptSuccess}
        onSuccess={onRefresh}
      />

      <RejectApplicationModal
        open={isRejectModalOpen}
        registrationId={
          selectedRow?.registrationId ?? detailData?.registrationId ?? ""
        }
        name={selectedRow?.name ?? detailData?.name ?? "-"}
        onClose={handleCloseRejectModal}
        onBack={handleBackFromRejectModal}
        onConfirm={handleRejectSuccess}
        onSuccess={onRefresh}
      />

      <Modal
        open={resultModalOpen}
        onCancel={handleCloseResultModal}
        footer={false}
        closable={false}
        centered
      >
        <Check
          size={72}
          className="mx-auto mb-4 sm:mb-10 text-[#1F993B]"
          aria-hidden="true"
        />

        <Title
          level={3}
          style={{
            fontSize: screens.xs ? 20 : 24,
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          {resultType === "accepted"
            ? "Permohonan berjaya diterima"
            : resultType === "deleted"
              ? "Permohonan telah berjaya dipadam"
              : "Permohonan telah ditolak"}
        </Title>
      </Modal>
    </>
  );
}
